import { filter, get, isEmpty, isEqual, omit, pickBy, sortBy } from 'lodash'
import {
  addNumberOfDaysInDate,
  convertDateTo_YYYY_MM_DD_T_HH_mm_ss,
  getAgeFromDateOfBirth
} from './dateUtil'
import moment from 'moment'
import {
  docTypes,
  ruleEntities,
  USCountryCode,
  REJECTION_VISIT_OTHERS_ID,
  VISITOR_STATUS_APPROVED,
  VISITOR_STATUS_PENDING_APPROVAL,
  VISITOR_STATUS_REJECTED,
  VISITS_FIELDS_TYPE,
  PORT_CODE_CCC
} from '../constants'
import { formatPhoneNumber, maskString, getGuestVisaDocuments } from './util'

export const boradcastMessageMapper = messages => {
  return (messages || []).map(msg => {
    const mappedMsg = {
      ...msg,
      messageId: msg.id
    }
    return omit(mappedMsg, 'id', '__typename')
  })
}

export const shipMapper = ships => {
  return (ships || [])
    .filter(result => result?.isOperational)
    .map(ship => {
      return {
        isOperational: get(ship, 'isOperational', null),
        imageUrl: get(ship, 'mediaItemId', null),
        label: get(ship, 'name', null),
        brandCode: get(ship, 'brandCode', null),
        shipCode: get(ship, 'code', null),
        shipName: get(ship, 'name', null),
        value: get(ship, 'code', null)
      }
    })
}

export const portMapper = ports => {
  return (ports || []).map(({ id, ...rest }) => {
    const mappedPort = {
      ...rest,
      portTerminalId: id
    }
    return omit(mappedPort, '__typename')
  })
}

export const portTerminalMapper = results => {
  return (results || []).map(port => {
    const embarkDate = get(port, 'voyage.embarkDate', '').split('T')
    const debarkDate = get(port, 'voyage.debarkDate', '').split('T')
    const mappedPort = {
      voyageId: get(port, 'voyage.id', ''),
      voyageName: get(port, 'voyage.name', ''),
      voyageNumber: get(port, 'voyage.number', ''),
      embarkDate: convertDateTo_YYYY_MM_DD_T_HH_mm_ss(
        embarkDate[0],
        embarkDate[1]
      ),
      debarkDate: convertDateTo_YYYY_MM_DD_T_HH_mm_ss(
        debarkDate[0],
        debarkDate[1]
      ),
      embarkPortCode: get(port, 'voyage.embarkPortCode', ''),
      portTerminalAddress: get(port, 'portTerminal.address', ''),
      portTerminalId: get(port, 'portTerminal.id', ''),
      portTerminalName: get(port, 'portTerminal.name', ''),
      portTerminalCoordinate: get(port, 'portTerminal.coordinates', ''),
      // code: get(port, 'port.code', ''),
      portCode: get(port, 'port.code', ''),
      portId: get(port, 'port.id', ''),
      name: get(port, 'port.name', '')
    }
    return mappedPort
    // return omit(mappedPort, 'id', '__typename')
  })
}
export const getGenderByCode = genderCode => {
  return genderCode === 'M' ? 'MALE' : genderCode === 'F' ? 'FEMALE' : 'OTHER'
}
export const getGenderCodeByName = genderCode => {
  return genderCode === 'MALE' ? 'M' : genderCode === 'FEMALE' ? 'F' : 'X'
}

export const formatGenderDisplay = gender => {
  if (!gender) return '-'
  if (gender === 'OTHER') return 'Another Gender'
  return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
}

export const alertOrMessagePayloadMapper = (body, isAlert = false) => {
  const payload = {
    addedDateOffset: get(body, 'addedDateOffset', null),
    createdByPersonId: get(body, 'createdBy', null),
    description: get(body, 'description', null),
    expiryDate: get(body, 'endDate', null),
    fromPersonName: get(body, 'fromPerson', null),
    isDeleted: get(body, 'isDeleted', false),
    isOverridable: get(body, 'isOverridable', false),
    isSoundEnabled: get(body, 'isSoundEnabled', false),
    saveAsTemplate: get(body, 'saveAsTemplate', false),
    sourceApplication: get(body, 'source', null),
    startDate: get(body, 'startDate', null)
  }
  if (isAlert) {
    return {
      ...payload,
      alertId: get(body, 'alertId', null),
      alertTypeCode: get(body, 'alertTypeCode', null),
      alertFromDepartMentId: get(body, 'departmentId', null),
      isOnPriority: get(body, 'isPriority', false),
      alertPersons: alertOrMessagePersonMapper(get(body, 'persons', []), true)
    }
  }
  return {
    ...payload,
    messageId: get(body, 'alertId', null),
    messagePersons: alertOrMessagePersonMapper(get(body, 'persons', []))
  }
}

export const alertOrMessagePersonMapper = (persons, isAlert) => {
  return (persons || []).map(person => {
    const personObj = {
      personId: get(person, 'personId', null),
      personTypeCode: get(person, 'personTypeCode', null),
      shipCode: get(person, 'propertyId', null),
      isCleared: get(person, 'isCleared', false),
      isDeleted: get(person, 'isDeleted', false),
      isRead: get(person, 'isRead', false)
    }
    if (isAlert) {
      return {
        ...personObj,
        alertId: get(person, 'alertId', null),
        personAlertId: get(person, 'personAlertId', null)
      }
    }
    return {
      ...personObj,
      messageId: get(person, 'alertId', null),
      personMessageId: get(person, 'personAlertId', null)
    }
  })
}

export const changeSetLogMapper = logs => {
  return logs.map(log => {
    const mappedSet = {
      ...log,
      new: log.newValue,
      old: log.oldValue
    }
    return omit(mappedSet, 'newValue', 'oldValue')
  })
}

export const embarkationLogMapper = logs => {
  return logs.map(log => {
    const changeSet = changeSetLogMapper(log.changeSet)
    const mappedLog = {
      ...log,
      changeLogId: log.id,
      deleted: log.isDeleted,
      changeSet
    }
    return omit(mappedLog, 'id', 'isDeleted', '__typename')
  })
}

export const voyageItinerariesMapper = itineraries => {
  return itineraries.map(({ id, ...rest }) => {
    const itinerary = {
      voyageItineraryId: id,
      ...rest
    }
    return omit(itinerary, '__typename')
  })
}

export const alertCodeMapper = alertCodes => {
  return alertCodes.map(({ id, code, name, ...rest }) => {
    const mappedCodes = {
      alertCodeId: id,
      code,
      value: code,
      name,
      label: name,
      ...rest
    }
    return omit(mappedCodes, ['__typename'])
  })
}
export const alertTypeMapper = alertTypes => {
  return alertTypes.map(({ id, code, name, ...rest }) => {
    return {
      alertTypeId: id,
      code,
      name,
      label: name,
      nameKey: name,
      value: code,
      ...rest
    }
  })
}

export const alertDepartmentMapper = alertDepartment => {
  return alertDepartment.map(({ id, code, name, ...rest }) => {
    return {
      code,
      departmentId: id,
      label: name,
      name,
      nameKey: name,
      value: id,
      ...rest
    }
  })
}

export const countryMapper = countries => {
  return countries.map(({ id, code, name, ...rest }) => {
    return {
      code,
      countryId: id,
      label: name,
      name,
      nameKey: name,
      value: code,
      ...rest
    }
  })
}

export const countryStateOrVisaTypeMapper = (countries, isVisaType) => {
  return countries.map(({ code, name, ...rest }) => {
    return {
      code,
      label: isVisaType ? `${code} - ${name}` : name,
      name,
      value: code,
      ...rest
    }
  })
}

export const docTypeMapper = documentTypes => {
  return documentTypes.map(({ id, code, name, ...rest }) => {
    const displayName = code === docTypes.esta ? docTypes.esta : name
    return {
      code,
      documentTypeId: id,
      label: displayName,
      name: displayName,
      nameKey: displayName,
      value: code,
      ...rest
    }
  })
}

export const gangwayLocationMapper = locations => {
  return locations.map(({ id, name, ...rest }) => {
    return {
      label: name,
      nameKey: name,
      locationId: id,
      value: id,
      ...rest
    }
  })
}

export const genderMapper = genders => {
  return genders.map(({ code, name, ...rest }) => {
    return {
      code: getGenderCodeByName(code),
      label: name,
      name,
      value: getGenderCodeByName(code),
      ...rest
    }
  })
}

export const referenceDataMapper = references => {
  const mappedReferences = references.map(({ code, name, type, ...rest }) => {
    const mappedRef = {
      code,
      value: code,
      sourceCode: code,
      label: name,
      name,
      referenceDataType: type,
      ...rest
    }
    return omit(mappedRef, '__typename')
  })
  return {
    guestCancellationReasons: filter(mappedReferences, {
      referenceDataType: 'RCR'
    }),
    visitorBoardingStatuses: filter(mappedReferences, {
      referenceDataType: 'VBT'
    })
  }
}

export const healthQuestionsMapper = questions => {
  return questions.map(({ question, ...rest }) => {
    const mappedQuestion = {
      name: question,
      ...rest
    }
    return omit(mappedQuestion, ['__typename'])
  })
}

export const loyaltyLevelTypesMapper = loyaltyLevelTypes => {
  return loyaltyLevelTypes.map(({ id, name, noOfCruiseNights }) => {
    return {
      name,
      label: name,
      loyaltyLevelTypeId: id,
      value: id,
      noOfCruiseNights
    }
  })
}

export const personTypesMapper = personTypes => {
  return personTypes.map(({ id, name, code, ...rest }) => {
    const personType = {
      code,
      value: code,
      name,
      label: name,
      personTypeId: id,
      ...rest
    }
    return omit(personType, '__typename')
  })
}

export const phoneTypesMapper = phoneTypes => {
  return phoneTypes.map(({ id, ...rest }) => {
    const phoneType = {
      phoneTypeId: id,
      ...rest
    }
    return omit(phoneType, '__typename')
  })
}
export const masterPortMapper = ports => {
  return ports.map(({ id, name, code }) => {
    const port = {
      portId: id,
      label: name,
      name,
      code,
      value: code
    }
    return omit(port, '__typename')
  })
}

export const reservationStatusMapper = reservationStatuses => {
  return reservationStatuses.map(({ name, code }) => {
    const port = {
      label: name,
      name,
      code
    }
    return omit(port, '__typename')
  })
}

export const shipDetailsMapper = ship => {
  if (isEmpty(ship)) return {}
  const mappedShip = {
    ...ship,
    shipCode: ship.code,
    value: ship.code,
    label: ship.name,
    shipName: ship.name,
    imageUrl: ship.mediaItemId
  }
  return omit(mappedShip, ['__typename', 'code', 'name', 'mediaItemId'])
}
export const shipTimeMapper = ship => {
  if (isEmpty(ship)) return {}
  const mappedShip = {
    ...ship,
    fromUtcDate: ship.utcDate,
    fromDateOffset: ship.utcOffset
  }
  return omit(mappedShip, ['__typename', 'utcOffset', 'utcDate'])
}

export const specialNeedsMapper = specialNeeds => {
  return specialNeeds.map(specialNeed => {
    const mappedNeed = {
      label: specialNeed.name,
      name: specialNeed.name,
      value: specialNeed.code,
      code: specialNeed.code,
      categoryKey: specialNeed.categoryCode,
      specialNeedTypeId: specialNeed.id
    }
    return omit(mappedNeed, '__typename')
  })
}

const toPreferenceMasterItem = preferenceValue => {
  if (!preferenceValue || preferenceValue.isDeleted || !preferenceValue.code) {
    return null
  }
  const name = preferenceValue.name || preferenceValue.nameKey || ''
  return {
    code: preferenceValue.code,
    name,
    label: name,
    value: preferenceValue.code
  }
}

/** Maps MasterData GraphQL preferences (or legacy REST) into code → name list. */
export const mapPreferenceMasterData = (preferencesResponse = {}) => {
  // GraphQL: { preferences: [ { values: [ { code, nameKey, isDeleted } ] } ] }
  const gqlParents = get(preferencesResponse, 'preferences', null)
  if (Array.isArray(gqlParents)) {
    return gqlParents.flatMap(preference => {
      const values = get(preference, 'values', []) || []
      return values.map(toPreferenceMasterItem).filter(Boolean)
    })
  }

  // Legacy REST: _embedded.preferences / preferencevalues
  const embedded = get(preferencesResponse, '_embedded', {})
  const nestedParents = get(embedded, 'preferences', [])
  const flatValues =
    get(embedded, 'preferencevalues', null) ||
    get(embedded, 'preferenceValues', null) ||
    []

  const fromParents = (nestedParents || []).flatMap(preference => {
    const values =
      get(preference, 'preferenceValues', null) ||
      get(preference, 'preferencevalues', null) ||
      []
    if (!values.length && preference.preferenceValueId) {
      return [toPreferenceMasterItem(preference)].filter(Boolean)
    }
    return values.map(toPreferenceMasterItem).filter(Boolean)
  })

  if (fromParents.length) return fromParents

  return (flatValues || []).map(toPreferenceMasterItem).filter(Boolean)
}

/** Maps MasterData GraphQL celebrations into code → name list. */
export const mapCelebrationMasterData = (celebrationsResponse = {}) => {
  const celebrations = get(celebrationsResponse, 'celebrations', []) || []
  return celebrations
    .filter(item => item && !item.isDeleted && item.code)
    .map(item => {
      const name = item.name || ''
      return {
        code: item.code,
        name,
        label: name,
        value: item.code
      }
    })
}
export const visitPurposeMapper = visitPurposes => {
  return visitPurposes.map(({ isDeleted, name, id }) => {
    const mappedNeed = {
      name,
      isDeleted,
      value: id,
      label: name,
      nameKey: name,
      visitPurposeId: id
    }
    return omit(mappedNeed, '__typename')
  })
}
export const visitDepartmentMapper = visitDepartments => {
  return visitDepartments.map(({ name, id }) => {
    const visitDepartment = {
      name,
      label: name,
      value: id,
      visitorDepartmentId: id
    }
    return omit(visitDepartment, '__typename')
  })
}
export const visitorRejectionReasonsMapper = reasonCategpries => {
  const reasonLists = []
  reasonCategpries.forEach(
    ({ rejectionReasons, id: rejectionReasonCategoryId }) => {
      ;(rejectionReasons || []).map(({ name, id }) => {
        const reason = {
          name,
          label: name,
          value: id,
          rejectionReasonId: id,
          rejectionReasonCategoryId
        }
        reasonLists.push(reason)
      })
    }
  )
  return reasonLists
}
export const visitorTypesMapper = visitorTypes => {
  return visitorTypes.map(({ name, code, id }) => {
    const visitorType = {
      name,
      label: name,
      value: code,
      code,
      visitorTypeId: id
    }
    return omit(visitorType, '__typename')
  })
}

export const masterDataReviewerMapper = reviewers => {
  const mappedReviewer = []
  reviewers.forEach(reviewer => {
    if (get(reviewer, 'crewMember', null)) {
      const tempData = {
        departmentCode: get(reviewer, 'crewMember.departmentCode', null),
        designation: get(reviewer, 'crewMember.designation', null),
        email: get(reviewer, 'crewMember.contactInfo.email', null),
        firstName: get(reviewer, 'crewMember.personalInfo.firstName', null),
        lastName: get(reviewer, 'crewMember.personalInfo.lastName', null),
        reviewerTeamMemberId: get(reviewer, 'crewMember.id', null),
        value: get(reviewer, 'crewMember.id', null)
      }
      if (!mappedReviewer.some(item => isEqual(item, tempData))) {
        mappedReviewer.push(tempData)
      }
    }
  })
  return mappedReviewer
}

export const itinerariesMapper = (itineraries, embarkDate) => {
  return itineraries
    .map(({ id, ...rest }) => {
      const formattedDate = addNumberOfDaysInDate(embarkDate, rest.itineraryDay)
      return {
        voyageItineraryId: id,
        itineraryDate: formattedDate,
        ...rest
      }
    })
    .sort((a, b) => {
      // when voyage is overnight (multiple port on same day)
      // Step 1: sort by itineraryDay
      if (a.itineraryDay !== b.itineraryDay) {
        return a.itineraryDay - b.itineraryDay
      }
      // Step 2: same day → sort by departureTime
      if (!a.departureTime) return 1
      if (!b.departureTime) return -1
      return new Date(a.departureTime) - new Date(b.departureTime)
    })
}

export const voyageMapper = voyages => {
  return voyages
    .map(({ name, number, id, itineraries, ...rest }) => {
      const voyage = {
        label: name,
        value: number,
        voyageName: name,
        voyageNumber: number,
        voyageId: id,
        voyageItineraries: itinerariesMapper(itineraries, rest.embarkDate),
        ...rest
      }
      return omit(voyage, '__typename')
    })
    .sort(
      (data1, data2) => new Date(data1.embarkDate) - new Date(data2.embarkDate)
    )
}

export const masterDataMapper = masterData => {
  const departments = alertDepartmentMapper(
    get(masterData, 'crewDepartments', [])
  )
  const referenceData = referenceDataMapper(
    get(masterData, 'searchReferenceData.referenceData', [])
  )
  const mappedVisaTypes = countryStateOrVisaTypeMapper(
    get(masterData, 'visaTypes.visaTypes', []),
    true
  )
  const mappedStates = countryStateOrVisaTypeMapper(
    get(masterData, 'countryStates.states', [])
  )
  return {
    alertCodes: alertCodeMapper(
      get(masterData, 'gangwayAlertCodes.gangwayAlertCodes', [])
    ),
    alertDepartments: departments,
    alertTypes: alertTypeMapper(get(masterData, 'personAlertTypes', [])),
    countries: countryMapper(get(masterData, 'countries.countries', [])),
    airlines: get(masterData, 'airlines.airlines', []),
    airports: get(masterData, 'airports.airports', []),
    usCountryStates: filter(mappedStates, e => e.countryCode === USCountryCode),
    usVisaTypes: filter(mappedVisaTypes, e => e.countryCode === USCountryCode),
    departments,
    documentTypes: docTypeMapper(get(masterData, 'documentTypes', [])),
    gangwayLocations: gangwayLocationMapper(
      get(masterData, 'gangwayLocationsByShipCode', [])
    ),
    genders: genderMapper(get(masterData, 'genders', [])),
    guestCancellationReasons: referenceData.guestCancellationReasons,
    healthQuestions: healthQuestionsMapper(
      get(masterData, 'healthQuestions', [])
    ),
    ipmDutyGroups: [],
    loyaltyLevelTypes: loyaltyLevelTypesMapper(
      get(masterData, 'loyaltyLevelTypes', [])
    ),
    personTypes: personTypesMapper(get(masterData, 'personTypes', [])),
    phoneTypes: phoneTypesMapper(get(masterData, 'phoneTypes', [])),
    ports: masterPortMapper(get(masterData, 'ports.ports', [])),
    preferences: mapPreferenceMasterData(get(masterData, 'preferences', {})),
    celebrations: mapCelebrationMasterData(get(masterData, 'celebrations', {})),
    reservationStatuses: reservationStatusMapper(
      get(masterData, 'reservationStatuses', [])
    ),
    shipDetails: shipDetailsMapper(get(masterData, 'shipByCode', {})),
    shipTimes: shipTimeMapper(get(masterData, 'shipTimeByCode', {})),
    specialNeeds: specialNeedsMapper(get(masterData, 'specialNeedTypes', [])),
    visitPurposes: visitPurposeMapper(get(masterData, 'visitPurposes', [])),
    visitorBoardingStatuses: referenceData.visitorBoardingStatuses,
    visitorDepartments: visitDepartmentMapper(
      get(masterData, 'visitorDepartments', [])
    ),
    visitorRejectionReasons: visitorRejectionReasonsMapper(
      get(masterData, 'visitorRejectionReasonCatagories', [])
    ),
    reviewerList: masterDataReviewerMapper(
      get(masterData, 'applicationReviewerUsers', [])
    ),
    submittedByList: masterDataReviewerMapper(
      get(masterData, 'applicationSubmitterUsers', [])
    ),
    visitorTypes: visitorTypesMapper(get(masterData, 'visitorTypes', [])),
    voyages: voyageMapper(get(masterData, 'voyagesWithPagination.voyages', []))
  }
}

export const slotsMapper = slots => {
  return slots.map(({ id, ...rest }) => {
    const mappedSlot = {
      ...rest,
      boardingNumberId: id
    }
    return omit(mappedSlot, '__typename')
  })
}
export const reservationGuestMapper = (guests, isVoyageWith = false) => {
  return guests.map(guest => {
    if (isVoyageWith) {
      return {
        reservationNumber: get(guest, 'reservationNumber', ''),
        reservationGuestId: get(guest, 'reservationGuestId', ''),
        firstName: get(guest, 'firstName', ''),
        lastName: get(guest, 'lastName', ''),
        stateroom: get(guest, 'stateroom', ''),
        isSameStateroom: get(guest, 'isSameStateroom', ''),
        securityPhotoMediaItemId: get(guest, 'securityPhotoMediaItemId', '')
      }
    }
    return {
      reservationNumber: get(guest, 'reservation.number', ''),
      reservationGuestId: get(guest, 'id', ''),
      firstName: get(guest, 'guest.personalInfo.firstName', ''),
      lastName: get(guest, 'guest.personalInfo.lastName', ''),
      securityPhotoMediaItemId: get(
        guest,
        'guest.personalInfo.securityImageId',
        ''
      )
    }
  })
}

export const reviewerMapper = reviewers => {
  return (reviewers || []).map(
    ({ id, createdByUser, reviewerCrewmemberId, ...rest }) => {
      const reviewer = {
        visitReviewerId: id,
        submittedBy: createdByUser,
        reviewerTeamMemberId: reviewerCrewmemberId,
        ...rest
      }
      return omit(reviewer, ['__typename', 'visitRejectionReasons'])
    }
  )
}

const mapApprovalReasons = visitReviewers => {
  const reasons = []
  if (visitReviewers && visitReviewers.length > 0) {
    const statusReasons = get(
      visitReviewers.find(
        item =>
          item.visitRejectionReasons && item.visitRejectionReasons.length > 0
      ),
      'visitRejectionReasons',
      ''
    )
    const othersReasonId = get(
      visitReviewers.find(item => item.rejectionReason),
      'rejectionReason',
      ''
    )
    if (!isEmpty(statusReasons)) {
      statusReasons.forEach(status => {
        reasons.push({
          visitRejectionReasonId: status.id,
          statusReason:
            status.rejectionReasonId === REJECTION_VISIT_OTHERS_ID
              ? othersReasonId
              : '',
          rejectionReasonId: status.rejectionReasonId,
          isDeleted: status.isDeleted && true
        })
      })
    }
  }
  return reasons
}

export const visitMapper = visits => {
  return (visits || []).map(
    ({
      id,
      specialNeeds,
      submittedByCrewMemberId,
      reviewerCrewMemberId,
      purposeId,
      shipCode,
      statusCode,
      reviewers,
      ...rest
    }) => {
      let stsCode = null
      let assistance = []
      if (specialNeeds && specialNeeds.length > 0) {
        specialNeeds.map(specialNeed => {
          assistance.push(omit(specialNeed, '__typename'))
        })
      }
      if (statusCode === 'pending') {
        stsCode = VISITOR_STATUS_PENDING_APPROVAL
      } else if (statusCode === 'approved') {
        stsCode = VISITOR_STATUS_APPROVED
      } else if (statusCode === 'rejected') {
        stsCode = VISITOR_STATUS_REJECTED
      }
      const mVisit = {
        visitId: id,
        reviewerTeamMemberId: reviewerCrewMemberId,
        assistance,
        submittedBy: submittedByCrewMemberId,
        validForShip: shipCode,
        visitPurposeId: purposeId,
        statusCode: stsCode,
        visitReviewers: reviewerMapper(reviewers),
        rejectionReasons: mapApprovalReasons(reviewers),
        shipCode,
        ...rest
      }
      return omit(mVisit, [
        'specialNeeds',
        'reviewers',
        'purposeId',
        '__typename'
      ])
    }
  )
}

export const visitorMapper = (visitors, visitFieldsType) => {
  return visitors.map(
    ({ id, departmentId, personalInfo, contactInfo, visits, ...rest }) => {
      let mVisits = (visits || []).map(visit => omit(visit, '__typename'))
      if (visitFieldsType === VISITS_FIELDS_TYPE.fullVisitsFields) {
        mVisits = visitMapper(visits || [])
      }
      const mappedVisitor = {
        phone: get(contactInfo, 'phones', [])
          .map(phone => phone.number)
          .join(','),
        visitorDepartmentId: departmentId,
        visitorId: id,
        age: get(personalInfo, 'age', ''),
        birthDate: get(personalInfo, 'birthDate', ''),
        citizenShipCountryCode: get(personalInfo, 'citizenshipCountryCode', ''),
        firstName: get(personalInfo, 'firstName', ''),
        lastName: get(personalInfo, 'lastName', ''),
        genderCode: getGenderCodeByName(get(personalInfo, 'gender', '')),
        securityPhotoMediaItemId: get(personalInfo, 'securityImageId', ''),
        visits: mVisits,
        ...rest
      }
      return omit(mappedVisitor, '__typename')
    }
  )
}

export const visitorDetailsMapper = visitors => {
  const { id, personalInfo, visits, phone, note, documents, ...rest } = visitors
  const mVisits = visitMapper(visits || [])
  const shipCode = get(visits, '[0].shipCode', undefined)
  const mappedVisitor = {
    age: get(personalInfo, 'age', ''),
    birthDate: get(personalInfo, 'birthDate', ''),
    citizenShipCountryCode: get(personalInfo, 'citizenshipCountryCode', ''),
    contactNumber: phone,
    profileImageUrl: get(personalInfo, 'securityImageId', ''),
    firstName: get(personalInfo, 'firstName', ''),
    lastName: get(personalInfo, 'lastName', ''),
    genderCode: getGenderCodeByName(get(personalInfo, 'gender', '')),
    personId: id,
    visitorNotes: note,
    visitorId: id,
    validForShip: shipCode,
    propertyId: shipCode,
    securityPhotoMediaItemId: get(personalInfo, 'securityImageId', ''),
    identifications: (documents || []).map(({ typeCode, ...rest }) => ({
      documentTypeCode: typeCode,
      ...rest
    })),
    visits: mVisits,
    ...rest
  }
  return omit(mappedVisitor, '__typename')
}

export const personalInfoMapper = (personInfo, keys) => {
  const obj = {}
  keys.forEach(key => {
    obj[key] = get(personInfo, key, '')
  })
  return obj
}

export const personMapper = (persons, guestBoardingNumbers = []) => {
  const personArr = []
  const personData = omit(persons, '__typename')
  const personKeys = Object.keys(personData)
  personKeys.map(key => {
    return (personData[key] || []).map(crew => {
      let personTypeCodeKey = 'personTypeCode'
      let personInfokey = 'personalInfo'
      let stsRoomkey = 'stateroom'
      let phoneNumberKey
      let emailKey
      let identifications = []
      if (key === 'reservationGuests') {
        personInfokey = 'guest.personalInfo'
        stsRoomkey = 'stateroomNumber'
        personTypeCodeKey = 'guest.personTypeCode'
        phoneNumberKey = 'guest.contactInfo.phones'
        emailKey = 'guest.contactInfo.email'
        identifications = getGuestVisaDocuments(crew).map(
          ({
            typeCode,
            documentTypeCode,
            issueCountryCode,
            number,
            numberOfEntries
          }) => ({
            documentTypeCode: typeCode || documentTypeCode,
            typeCode: typeCode || documentTypeCode,
            number,
            issueCountryCode,
            numberOfEntries
          })
        )
      } else if (key === 'crewMembers') {
        identifications = get(crew, 'documents', []).map(
          ({ typeCode, number }) => ({
            documentTypeCode: typeCode,
            number
          })
        )
      }
      let boardingNumbers = []
      if (!isEmpty(guestBoardingNumbers)) {
        boardingNumbers = guestBoardingNumbers.filter(
          bNumber => bNumber.reservationGuestId === crew.id
        )
      }
      const mCrew = omit(
        {
          ...crew,
          boardingNumbers,
          personId: get(crew, 'id', ''),
          personTypeCode: get(crew, personTypeCodeKey, '') || 'V',
          stateroom: get(crew, stsRoomkey, ''),
          phoneNumber: get(crew, phoneNumberKey, [])
            .map(phone => formatPhoneNumber(phone))
            .filter(Boolean)
            .join(','),
          email: emailKey
            ? get(crew, emailKey, '')
            : get(crew, 'contactInfo.email', ''),
          departmentKey: get(crew, 'departmentName', ''),
          identifications,
          ...(key === 'crewMembers' && {
            teamMemberNumber: get(crew, 'crewMemberNumber', ''),
            citizenShipCountryCode: get(
              crew,
              'personalInfo.citizenshipCountryCode',
              ''
            ),
            genderCode: getGenderCodeByName(
              get(crew, 'personalInfo.gender', '')
            ),
            status: get(crew, 'boardingStatusKey', ''),
            boardingStatus: get(crew, 'boardingStatusKey', '')
          })
        },
        '__typename'
      )
      const personalInfo = personalInfoMapper(get(crew, personInfokey, {}), [
        'age',
        'securityImageId',
        'gender',
        'birthDate',
        'citizenshipCountryCode',
        'firstName',
        'lastName'
      ])
      const age = get(personalInfo, 'age')
      personArr.push({
        ...mCrew,
        ...personalInfo,
        ...(key === 'crewMembers' &&
          (age === null || age === undefined || age === '') &&
          get(personalInfo, 'birthDate', '') && {
            age: getAgeFromDateOfBirth(get(personalInfo, 'birthDate'))
          })
      })
    })
  })
  return personArr
}
export const ipmDepartmentMapper = departments => {
  return departments.map(({ id, departmentName, ...rest }) => {
    const department = {
      ipmPositionDepartmentId: id,
      departmentKey: departmentName,
      ...rest
    }
    return omit(department, '__typename')
  })
}
export const ipmDutiesMapper = duties => {
  return duties.map(({ id, ...rest }) => {
    return {
      ipmPositionId: id,
      ...rest
    }
  })
}

export const ipmSafetyPositionDetailsMapper = safetyPositions => {
  return safetyPositions.map(
    ({ id, ipmPositionsDuties, departmentName, ...rest }) => {
      const safetyPosition = {
        ipmSafetyPositionDetailId: id,
        departmentKey: departmentName,
        ipmPositionsDuties: ipmDutiesMapper(ipmPositionsDuties || []),
        ...rest
      }
      return omit(safetyPosition, '__typename')
    }
  )
}

export const ipmListMapper = ipms => {
  const records = ipms.map(ipm => {
    const record = {
      ...ipm,
      ipmPositionDepartments: ipmDepartmentMapper(
        get(ipm, 'ipmPositionDepartments', [])
      ),
      ipmSafetyPositionDetails: ipmSafetyPositionDetailsMapper(
        get(ipm, 'ipmSafetyPositionDetails', [])
      )
    }
    return omit(record, '__typename')
  })
  return records
}

export const cbpComparisonResultMapper = (
  response,
  isEnoadBatchComparison,
  { shouldUnMaskedSailor, shouldUnMaskedCrew }
) => {
  const mappedResponse = { crew: [], sailor: [] }
  if (!Array.isArray(response) || isEmpty(response)) {
    return mappedResponse
  }

  response.forEach(res => {
    const mappedArray =
      res.type === 'Crew' ? mappedResponse.crew : mappedResponse.sailor
    const shouldMask =
      res.type === 'Crew' ? shouldUnMaskedCrew : shouldUnMaskedSailor
    const comparisonResult = res.comparisonresult
    Object.values(comparisonResult).map(comparisonResultKey => {
      let source =
        !isEmpty(comparisonResultKey.source) && comparisonResultKey.source
      let target =
        !isEmpty(comparisonResultKey.target) && comparisonResultKey.target
      const obj = source || target
      const identificationKey =
        res.type === 'Crew' ? 'ID_NUM' : 'PASSENGER_RECORD_LOCATOR'
      const identificationNo = get(obj, identificationKey, '-')
      const fullName = `${get(obj, 'FIRST_NAME', '')} ${get(
        obj,
        'LAST_NAME',
        ''
      )}`
      let sourceDifference = get(comparisonResultKey, 'sourceDifference', {})
      let targetDifference = get(comparisonResultKey, 'targetDifference', {})

      const sourceBirthDate = maskString(
        get(source, 'BIRTH_DT', undefined),
        shouldMask,
        { showEnd: 5, showStart: 0 }
      )
      const targetBirthDate = maskString(
        get(target, 'BIRTH_DT', undefined),
        shouldMask,
        { showEnd: 5, showStart: 0 }
      )
      const sourceIdNum = maskString(
        get(source, 'ID_NUM', undefined),
        shouldMask
      )
      const targetIdNum = maskString(
        get(target, 'ID_NUM', undefined),
        shouldMask
      )
      const sourceSecondaryIdNum = maskString(
        get(source, 'SECONDARY_ID_NUM', undefined),
        shouldMask
      )
      const targetSecondaryIdNum = maskString(
        get(target, 'SECONDARY_ID_NUM', undefined),
        shouldMask
      )
      const sourceAgencyPhone = maskString(
        get(source, 'AGENCY_PHONE', undefined),
        shouldMask
      )
      const targetAgencyPhone = maskString(
        get(target, 'AGENCY_PHONE', undefined),
        shouldMask
      )
      const sourceTravelAgencyPhone = maskString(
        get(source, 'TRAVEL_AGENCY_PHONE', undefined),
        shouldMask
      )
      const targetTravelAgencyPhone = maskString(
        get(target, 'TRAVEL_AGENCY_PHONE', undefined),
        shouldMask
      )
      const sourcePrimaryContactPhone = maskString(
        get(source, 'PRIMARY_CONTACT_PHONE', undefined),
        shouldMask
      )
      const targetPrimaryContactPhone = maskString(
        get(target, 'PRIMARY_CONTACT_PHONE', undefined),
        shouldMask
      )
      const sourceContactPhone = maskString(
        get(source, 'CONTACT_PHONE', undefined),
        shouldMask
      )
      const targetContactPhone = maskString(
        get(target, 'CONTACT_PHONE', undefined),
        shouldMask
      )
      source = {
        ...source,
        BIRTH_DT: sourceBirthDate,
        ID_NUM: sourceIdNum,
        AGENCY_PHONE: sourceAgencyPhone,
        TRAVEL_AGENCY_PHONE: sourceTravelAgencyPhone,
        PRIMARY_CONTACT_PHONE: sourcePrimaryContactPhone,
        CONTACT_PHONE: sourceContactPhone,
        SECONDARY_ID_NUM: sourceSecondaryIdNum
      }
      target = {
        ...target,
        BIRTH_DT: targetBirthDate,
        ID_NUM: targetIdNum,
        AGENCY_PHONE: targetAgencyPhone,
        TRAVEL_AGENCY_PHONE: targetTravelAgencyPhone,
        PRIMARY_CONTACT_PHONE: targetPrimaryContactPhone,
        CONTACT_PHONE: targetContactPhone,
        SECONDARY_ID_NUM: targetSecondaryIdNum
      }
      source = pickBy(source, v => v)
      target = pickBy(target, v => v)

      if (!isEmpty(sourceDifference) && !isEmpty(targetDifference)) {
        const sourceDiffBirthDate = maskString(
          get(sourceDifference, 'BIRTH_DT', undefined),
          shouldMask,
          { showEnd: 5, showStart: 0 }
        )
        const targetDiffBirthDate = maskString(
          get(targetDifference, 'BIRTH_DT', undefined),
          shouldMask,
          { showEnd: 5, showStart: 0 }
        )
        const sourceDiffIdNum = maskString(
          get(sourceDifference, 'ID_NUM', undefined),
          shouldMask
        )
        const targetDiffIdNum = maskString(
          get(targetDifference, 'ID_NUM', undefined),
          shouldMask
        )
        const sourceDiffAgencyPhone = maskString(
          get(sourceDifference, 'AGENCY_PHONE', undefined),
          shouldMask
        )
        const targetDiffAgencyPhone = maskString(
          get(targetDifference, 'AGENCY_PHONE', undefined),
          shouldMask
        )
        const sourceDiffTravelAgencyPhone = maskString(
          get(sourceDifference, 'TRAVEL_AGENCY_PHONE', undefined),
          shouldMask
        )
        const targetDiffTravelAgencyPhone = maskString(
          get(targetDifference, 'TRAVEL_AGENCY_PHONE', undefined),
          shouldMask
        )
        const sourceDiffPrimaryContactPhone = maskString(
          get(sourceDifference, 'PRIMARY_CONTACT_PHONE', undefined),
          shouldMask
        )
        const targetDiffPrimaryContactPhone = maskString(
          get(targetDifference, 'PRIMARY_CONTACT_PHONE', undefined),
          shouldMask
        )
        const sourceDiffContactPhone = maskString(
          get(sourceDifference, 'CONTACT_PHONE', undefined),
          shouldMask
        )
        const targetDiffContactPhone = maskString(
          get(targetDifference, 'CONTACT_PHONE', undefined),
          shouldMask
        )
        const sourceDiffSecondaryIdNum = maskString(
          get(sourceDifference, 'SECONDARY_ID_NUM', undefined),
          shouldMask
        )
        const targetDiffSecondaryIdNum = maskString(
          get(targetDifference, 'SECONDARY_ID_NUM', undefined),
          shouldMask
        )
        sourceDifference = {
          ...sourceDifference,
          BIRTH_DT: sourceDiffBirthDate,
          ID_NUM: sourceDiffIdNum,
          AGENCY_PHONE: sourceDiffAgencyPhone,
          TRAVEL_AGENCY_PHONE: sourceDiffTravelAgencyPhone,
          PRIMARY_CONTACT_PHONE: sourceDiffPrimaryContactPhone,
          CONTACT_PHONE: sourceDiffContactPhone,
          SECONDARY_ID_NUM: sourceDiffSecondaryIdNum
        }
        targetDifference = {
          ...targetDifference,
          BIRTH_DT: targetDiffBirthDate,
          ID_NUM: targetDiffIdNum,
          AGENCY_PHONE: targetDiffAgencyPhone,
          TRAVEL_AGENCY_PHONE: targetDiffTravelAgencyPhone,
          PRIMARY_CONTACT_PHONE: targetDiffPrimaryContactPhone,
          CONTACT_PHONE: targetDiffContactPhone,
          SECONDARY_ID_NUM: targetDiffSecondaryIdNum
        }
        sourceDifference = pickBy(sourceDifference, v => v)
        targetDifference = pickBy(targetDifference, v => v)
        Object.entries(sourceDifference).map(([key, value]) => {
          mappedArray.push({
            fullName,
            identificationNo,
            fieldName: key,
            valueInEnoad: value,
            batchAPI: targetDifference[key],
            source,
            sourceDifference,
            target,
            targetDifference
          })
        })
      } else {
        let type = 'NA'
        if (isEmpty(source)) {
          type = isEnoadBatchComparison ? 'Batch' : 'VXP eNOAD'
        } else if (isEmpty(target)) {
          type = isEnoadBatchComparison ? 'eNOAD' : 'MXP eNOAD'
        }
        const mappedObj = {
          fullName,
          isEnoadBatchComparison,
          identificationNo,
          fieldName: `Record does not exist in ${type}`,
          valueInEnoad: 'NA',
          batchAPI: 'NA',
          source,
          target
        }
        mappedArray.push(mappedObj)
      }
    })
  })
  return mappedResponse
}

export const rulesMapper = rules => {
  const mappedRules = {}
  rules.forEach(rule => {
    const fieldNameStr = rule?.fieldName?.split('-')
    if (!isEmpty(fieldNameStr)) {
      mappedRules[fieldNameStr[1]] = omit(rule, '__typename')
    }
  })
  return mappedRules
}

export const buildVisitorRuleSet = (body, options = {}) => {
  const { editVisitor = false, addVisit = false } = options
  const visits = get(body, 'visits', [])
  const todayDate = moment().startOf('day')
  const currentOrFutureVisits = visits.filter(visit => {
    const startDate = get(visit, 'startDate', '')
    const visitStartDate = moment(startDate).startOf('day')
    return (
      visitStartDate.isValid() &&
      (visitStartDate.isSame(todayDate) || visitStartDate.isAfter(todayDate))
    )
  })
  const nearestVisitsByStartDate = sortBy(currentOrFutureVisits, visit => {
    const startDate = get(visit, 'startDate', '')
    return moment(startDate)
      .startOf('day')
      .diff(todayDate, 'days')
  })
  const selectedVisits = editVisitor
    ? nearestVisitsByStartDate.length > 0
      ? [nearestVisitsByStartDate[0]]
      : []
    : addVisit
    ? visits
    : isEmpty(visits)
    ? [{}]
    : visits
  const visitCollection = selectedVisits.map(visit => ({
    boardingTypeCode: get(visit, 'boardingTypeCode', ''),
    startDate: get(visit, 'startDate', ''),
    endDate: get(visit, 'endDate', ''),
    visitPurpose: get(visit, 'purpose', ''),
    propertyId: Array.isArray(get(visit, 'validForShip', ''))
      ? get(visit, 'validForShip[0]', '')
      : get(visit, 'validForShip', ''),
    portCode: get(visit, 'isSeaDay', false)
      ? PORT_CODE_CCC
      : get(visit, 'portCode', ''),
    isNeedEscort: get(visit, 'needEscort', false),
    reviewerName: get(visit, 'reviewerTeamMemberId[0]', ''),
    submittedBy: get(body, 'submittedByName', '')
  }))

  const documentTypeCode = get(body, 'identifications[0].documentTypeCode', '')
  const hasUsVisaDocument = documentTypeCode === docTypes.usVisa
  const documentNumber = get(body, 'identifications[0].number', '')

  const docImgUrl = get(body, 'identifications[0].imageId', '')
  const docMediaId = docImgUrl ? docImgUrl.split('/').pop() : ''
  const profileImgUrl = get(body, 'profileImageUrl', '')
  const profileMediaId = profileImgUrl ? profileImgUrl.split('/').pop() : ''

  const visaCollection = hasUsVisaDocument
    ? [
        {
          visaNumber: documentNumber,
          scannedCopyMediaItemId: docMediaId,
          visaTypeCode: get(body, 'identifications[0].subTypeCode', '')
        }
      ]
    : []

  const documentCollection = hasUsVisaDocument
    ? [{ documentTypeCode }]
    : [
        {
          documentTypeCode,
          documentNumber,
          scannedCopyMediaItemId: docMediaId,
          issuingState: get(body, 'identifications[0].issueStateCode', ''),
          issuingCountry: get(body, 'identifications[0].issueCountryCode', '')
        }
      ]

  const ruleSet = [
    {
      className: ruleEntities.visitorPersonalInformation,
      request: {
        securityPhotoMediaItemId: profileMediaId,
        firstName: get(body, 'firstName', ''),
        lastName: get(body, 'lastName', ''),
        genderCode: get(body, 'genderCode', ''),
        companyName: get(body, 'companyName', ''),
        companyContactNumber: get(body, 'contactNumber', ''),
        citizenShipCountryCode: get(body, 'citizenShipCountryCode', ''),
        birthDate: get(body, 'dateOfBirth', ''),
        age: getAgeFromDateOfBirth(get(body, 'dateOfBirth', null)),
        note: get(body, 'visitorNotes', ''),
        visitorTypeCode: get(body, 'visitorTypeCode', ''),
        departmentPOC: get(body, 'departmentPOC', '')
      }
    },
    {
      className: ruleEntities.visitorVisitInformation,
      request: { visitCollection }
    },
    {
      className: ruleEntities.visitorIdentification,
      request: {
        visaCollection,
        documentCollection
      }
    }
  ]

  return { ruleSet }
}
